import type { Item } from '../dto/searchResultHit';
import { getExtname } from '../platformAbstraction/pathComposition';
import type { MediaAvailability } from '../dto/mediaAvailability';
import { StreamRoleId } from '../dto/mediaAvailability';
import { SEGMENTED_HIGHRES_EXTENSION } from '../dto/mediaDtos';
import type { ItemArtefact } from '../dto/transferRequestDtos';
import type { Fraction } from './fractionType';
import { invariant } from './invariant';
import { dirname, makeDirPAsync, readFileAsync, writeFileAsync, doesPathExist } from './fs';
import { getEditProxyExtension } from './item';
import { isCurrentlyLive } from '../item/media';
import type { Logger } from '../types/logger';
import type { LocalSegmentedMediaState } from './mediaState';
import { getSegmentIndexFromMediaTime, getSegmentIndexFromSegmentTime } from './segmentIndexing';
import type { NodeApi } from './detectApi';
import { getMediaDestination, type MediaDestination } from '../kelda/mediaDestination';

export const humanReadableFileSize = (bytes: number, returnHtml = false): string => {
  if (isNaN(bytes)) {
    if (returnHtml) {
      return '<i>unknown</i>';
    }
    return 'unknown';
  }
  const segmentSize = 1024;
  if (Math.abs(bytes) < segmentSize) {
    if (returnHtml) {
      return `<span class="file-size-value">${bytes}</span><span class="file-size-unit">B</span>`;
    }
    return bytes + ' B';
  }
  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let u = -1;
  do {
    bytes /= segmentSize;
    ++u;
  } while (Math.abs(bytes) >= segmentSize && u < units.length - 1);

  if (returnHtml) {
    return `<span class="file-size-value">${bytes.toFixed(1)}</span><span class="file-size-unit">${units[u]}</span>`;
  }
  return bytes.toFixed(1) + ' ' + units[u];
};

/**
 * Converts MB to Bytes
 * @param sizeInMB
 */
export const convertMbToBytes = (sizeInMB: number): number => {
  return sizeInMB * 1024 * 1024;
};


export enum FileExtensions {
  IsoBaseMediaFileSegment = '.m4s'
}

export function getSegmentFileNameByIndex(itemId: string, streamId: StreamRoleId, segmentIndex: number) {
  return itemId + '_' + streamId + '_' + segmentIndex + FileExtensions.IsoBaseMediaFileSegment;
}

/**
 * For a given item and end point of range (optional), checks for locally available segments and
 * returns the indexes that need to be downloaded.
 * @param endTime End point of required range (in seconds), if instead of the full clip,
 * only a part of the media needs to be downloaded
 *
 * Returns a LocalSegmentedMediaState or undefined if the item artefact is not segmented.
 */
export async function getLocalSegmentedMediaStates(
  item: Pick<Item, 'id' | 'liveRecording' | 'media' | 'editProxyFormat' | 'isLive'>,
  artefact: ItemArtefact,
  partialMediaManager: PartialMediaManager,
  endTime: number | undefined
): Promise<LocalSegmentedMediaState | undefined> {
  const itemMedia = item.media;
  if (!itemMedia) {
    return undefined;
  }

  const isSegmented = (artefact === 'proxy')
  || (artefact === 'highres' && (itemMedia.codecsByStreamId[StreamRoleId.LiveRecordHighresTs] !== undefined || itemMedia.codecsByStreamId[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined));

  if (!isSegmented) {
    return undefined;
  }

  const isMpegTs = (itemMedia.codecsByStreamId[StreamRoleId.LiveRecordWebproxyTs] !== undefined || itemMedia.codecsByStreamId[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined);
  const hasInitSegment = !isMpegTs;
  const states: LocalSegmentedMediaState = {
    firstSegmentIndex: 0,
    recordPartialMediaInfo: false,
    partialLocalPaths: partialMediaManager.getRegularLocalDownloadPaths(),
    segmentType: isMpegTs ? 'ts' : 'mp4',
  };

  const lastSegmentIndexOfMedia =
    getSegmentIndexFromSegmentTime(itemMedia.segmentCount - 1, hasInitSegment);


  states.lastSegmentIndex = (endTime !== undefined) ?
    getSegmentIndexFromMediaTime(endTime, itemMedia.segmentDuration, hasInitSegment) :
    lastSegmentIndexOfMedia;

  const lastSegmentIndexLocally = await partialMediaManager.getLocallyAvailableMaxSegmentIndex();

  if (lastSegmentIndexLocally !== undefined) {
    // parts of it downloaded already
    if (states.lastSegmentIndex <= lastSegmentIndexLocally) {
      // current import falls within the available range. use the largest available file.
      states.lastSegmentIndex = lastSegmentIndexLocally;
    } else {
      // current import needs more segments than what is available. download and augment the local file.
      states.firstSegmentIndex = lastSegmentIndexLocally + 1;
    }
  }

  states.firstSegmentIndex = Math.min(states.firstSegmentIndex, lastSegmentIndexOfMedia);
  states.lastSegmentIndex = Math.min(states.lastSegmentIndex, lastSegmentIndexOfMedia);

  // Check if we are downloading the whole proxy and it is not streaming any more. Then we can just use the
  // regular location in order to re-use existing logic for handling locally available media, like showing
  // green checkmark etc.
  const useRegularDownloadLocation = !isCurrentlyLive(item) && (endTime === undefined);
  if (!useRegularDownloadLocation) {
    states.partialLocalPaths = partialMediaManager.getPartialMediaLocalPaths(states.lastSegmentIndex) ?? states.partialLocalPaths;
    states.recordPartialMediaInfo = true;
  }

  return states;
}

export interface PartialMediaInfo {
  locallyAvailableMaxSegmentIndex: number
}

/**
 * Encapsulates functionality required to derive local state of partial segmented media
 */
export class PartialMediaManager {
  private fileOps: {
    readFileAsync: typeof readFileAsync;
    makeDirPAsync: typeof makeDirPAsync;
    writeFileAsync: typeof writeFileAsync;
    doesPathExist: typeof doesPathExist;
   };

  constructor(
    private nodeApi: NodeApi,
    private logger: Logger,
    private readonly item: Pick<Item, 'id' | 'editProxyFormat' | 'originalFileName'>,
    private readonly downloadPath: string,
    private readonly pathSeparator: string,
    private readonly artefact: ItemArtefact,
    fileOperations?: PartialMediaManager['fileOps']
  ) {
    this.fileOps = fileOperations ?? {
      readFileAsync, makeDirPAsync, writeFileAsync, doesPathExist
    };
  }

  private getPartialMediaBaseDir() {
    const baseDir = this.downloadPath;
    invariant(baseDir, 'Download path not found');

    return `${baseDir}${this.pathSeparator}partial`;
  }

  private getPartialMediaInfoFilePath() {
    const suffix = (this.artefact === 'highres') ? '-highres' : '';
    return `${this.getPartialMediaBaseDir()}${this.pathSeparator}${this.item.id}-info${suffix}.json`;
  }

  private async getPartialMediaInfo(): Promise<PartialMediaInfo | null> {
    const filePath = this.getPartialMediaInfoFilePath();
    let contents: string | undefined = undefined;
    try {
      contents = await this.fileOps.readFileAsync(this.nodeApi, filePath, 'utf-8') as string;
    } catch (error) {
      contents = undefined;
    }

    if (!contents) {
      return null;
    }

    let info: PartialMediaInfo | null = null;
    try {
      info = JSON.parse(contents);
    } catch (err: any) {
      this.logger.error(`Error parsing ${filePath}: ${contents}`);
    }
    return info;
  }

  async getLocallyAvailableMaxSegmentIndex() {
    const info = await this.getPartialMediaInfo();
    const index = info?.locallyAvailableMaxSegmentIndex;
    if (index === undefined || !Number.isInteger(index) || index < 0) {
      return;
    }

    // check actual file path existence, because the info file can go out of sync for bugs or other reasons
    const mediaFilePaths = this.getPartialMediaLocalPaths(index);
    if (!mediaFilePaths) {
      return;
    }

    if (!await this.fileOps.doesPathExist(this.nodeApi, this.logger, mediaFilePaths.legacyDestinationPath, false)
       && !await this.fileOps.doesPathExist(this.nodeApi, this.logger, mediaFilePaths.subfoldersDestinationPath, false)) {
      this.logger.debug(`Ignoring partial media info since nieither ${mediaFilePaths.legacyDestinationPath} nor ${mediaFilePaths.subfoldersDestinationPath} exists`);
      return;
    }

    return index;
  }

  async setLocallyAvailableMaxSegmentIndex(segmentIndex: number) {
    let info = await this.getPartialMediaInfo();
    if (!info) {
      info = {
        locallyAvailableMaxSegmentIndex: segmentIndex,
      };
    } else if (info.locallyAvailableMaxSegmentIndex < segmentIndex) {
      info.locallyAvailableMaxSegmentIndex = segmentIndex;
    }

    const filePath = this.getPartialMediaInfoFilePath();
    await this.fileOps.makeDirPAsync(this.nodeApi, dirname(this.nodeApi, filePath));
    await this.fileOps.writeFileAsync(this.nodeApi, filePath, JSON.stringify(info, null, 2));
  }

  private getProxyPartialMediaLocalPaths(lastSegmentIndex: number): MediaDestination {
    const sep = this.pathSeparator;
    return getMediaDestination(`${this.downloadPath}${sep}partial`, `${this.item.id}-${lastSegmentIndex}-proxy${getEditProxyExtension(this.item)}`, sep);
  }

  private getHighresPartialMediaLocalPaths(lastSegmentIndex: number): MediaDestination {
    const sep = this.pathSeparator;
    return getMediaDestination(`${this.downloadPath}${sep}partial`, `${this.item.id}-${lastSegmentIndex}-highres${SEGMENTED_HIGHRES_EXTENSION}`, sep);
  }

  getRegularLocalDownloadPaths(): MediaDestination {
    const artefactSuffix = this.artefact === 'proxy' ? '-proxy' : '';

    /*
      TODO: The property this.item.originalFileName is only the basename of the
      file, which means that any "/" or "\\" inside of this string is intended
      to be part of the basename, and not be a path separator.

      It is therefore technically incorrect to call getExtname() here which
      transitively performs another getBasename() call with a given path
      separator, which may differ from the original context where the basename
      was captured.

      We should instead call a variant of getExtname() that does not do any
      additional interpretation of path separators.
    */
    const pathSep = '/';
    const ext = getExtname(this.item.originalFileName ?? '', pathSep);
    return getMediaDestination(this.downloadPath, `${this.item.id}${artefactSuffix}${ext}`, this.pathSeparator);
  }

  getPartialMediaLocalPaths(lastSegmentIndex: number): MediaDestination | undefined {
    if (this.artefact === 'proxy') {
      return this.getProxyPartialMediaLocalPaths(lastSegmentIndex);
    } else if (this.artefact === 'highres') {
      return this.getHighresPartialMediaLocalPaths(lastSegmentIndex);
    } else {
      // not ready yet
    }
  }

  getSep() {
    return this.pathSeparator;
  }
}

/**
 * For a given time range in seconds and segment duration, returns the segment indices where the start and end position fall in.
 * Result also includes the adjusted start position in seconds relative to the starting segment.
 */
export const getSegmentInfoFromTimeRange = (rangeInSeconds: { start: number, end: number }, segmentDuration: Fraction) => {
  const firstSegmentIndex = Math.floor(rangeInSeconds.start * segmentDuration.denominator / segmentDuration.numerator);
  const segmentStartDurationInSeconds = segmentDuration.numerator * firstSegmentIndex / segmentDuration.denominator;

  let offset = rangeInSeconds.start - segmentStartDurationInSeconds;

  if (offset < 0) offset = 0;

  const lastSegmentIndex = Math.floor(rangeInSeconds.end * segmentDuration.denominator / segmentDuration.numerator);

  return {
    firstSegmentIndex,
    lastSegmentIndex,
    adjustedStartTimeInSeconds: offset,
  };
};

export const getTotalSizeByStreamId = (media: MediaAvailability, streamId: StreamRoleId): number | undefined => {
  const sizePerByte = media.sizeByStreamId?.[streamId];
  if (!sizePerByte || isNaN(sizePerByte ?? NaN)) return undefined;
  return media.segmentCount * sizePerByte;
};
